up.form = (function () {
    const u = up.util;
    const e = up.element;
    const config = new up.Config(() => ({
        groupSelectors: ['[up-form-group]', 'fieldset', 'label', 'form'],
        fieldSelectors: ['select', 'input:not([type=submit], [type=image], [type=button])', 'button[type]:not([type=submit], [type=button])', 'textarea'],
        submitSelectors: ['form:is([up-submit], [up-target], [up-layer], [up-transition])'],
        noSubmitSelectors: ['[up-submit=false]', '[target]', e.crossOriginSelector('action')],
        submitButtonSelectors: ['input[type=submit]', 'input[type=image]', 'button[type=submit]', 'button:not([type])'],
        genericButtonSelectors: ['input[type=button]', 'button[type=button]'],
        validateBatch: true,
        watchInputEvents: ['input', 'change'],
        watchInputDelay: 0,
        watchChangeEvents: ['change'],
        watchableEvents: ['input', 'change'],
        arrayParam: (name) => name.endsWith('[]'),
    }));
    function findFormElements(root, selectorFn) {
        root = e.get(root);
        let elements = e.subtree(root, selectorFn());
        if (root.matches('form[id]')) {
            const formReference = e.attrSelector('form', root.getAttribute('id'));
            const externalElementsSelector = selectorFn(formReference);
            const externalElements = up.fragment.all(externalElementsSelector, { layer: root });
            elements = u.uniq([...elements, ...externalElements]);
        }
        return elements;
    }
    const [fieldSelector, isField] = config.selectorFns('fieldSelectors');
    function findFields(root) {
        return findFormElements(root, fieldSelector);
    }
    function findFieldsAndButtons(container) {
        return [
            ...findFields(container),
            ...findSubmitButtons(container),
            ...findGenericButtons(container),
        ];
    }
    const [submitButtonSelector, isSubmitButton] = config.selectorFns('submitButtonSelectors');
    function findSubmitButtons(root) {
        return findFormElements(root, submitButtonSelector);
    }
    const genericButtonSelector = config.selectorFn('genericButtonSelectors');
    function findGenericButtons(root) {
        return findFormElements(root, genericButtonSelector);
    }
    const submit = up.mockable((form, options) => {
        return up.render(submitOptions(form, options));
    });
    function submitOptions(form, options, parserOptions) {
        form = up.fragment.get(form);
        form = getForm(form);
        options = u.options(options);
        let parser = new up.OptionsParser(form, options, parserOptions);
        parser.include(destinationOptions);
        parser.string('failTarget', { default: up.fragment.tryToTarget(form) });
        options.guardEvent || (options.guardEvent = up.event.build('up:form:submit', {
            submitButton: options.submitButton,
            log: 'Submitting form',
            params: options.params,
            form,
        }));
        options.origin || (options.origin = up.viewport.focusedElementWithin(form) || options.submitButton || form);
        options.activeElements = u.uniq([options.origin, options.submitButton, form].filter(u.isElement));
        parser.include(up.link.followOptions);
        Object.assign(options, submitButtonOverrides(options.submitButton));
        return options;
    }
    function watchOptions(field, options, parserOptions = {}) {
        var _a, _b, _c;
        options = u.options(options);
        (_a = parserOptions.closest) !== null && _a !== void 0 ? _a : (parserOptions.closest = true);
        (_b = parserOptions.attrPrefix) !== null && _b !== void 0 ? _b : (parserOptions.attrPrefix = 'up-watch-');
        let parser = new up.OptionsParser(field, options, parserOptions);
        parser.include(up.status.statusOptions, parserOptions);
        parser.string('event');
        parser.number('delay');
        let config = up.form.config;
        if (options.event === 'input') {
            options.event = u.evalOption(config.watchInputEvents, field);
            (_c = options.delay) !== null && _c !== void 0 ? _c : (options.delay = config.watchInputDelay);
        }
        else if (options.event === 'change') {
            options.event = u.evalOption(config.watchChangeEvents, field);
        }
        options.origin || (options.origin = field);
        return options;
    }
    function validateOptions(field, options, parserOptions = {}) {
        options = u.options(options);
        let parser = new up.OptionsParser(field, options, Object.assign(Object.assign({}, parserOptions), { closest: true, attrPrefix: 'up-validate-' }));
        parser.string('url');
        parser.string('method', { normalize: u.normalizeMethod });
        parser.boolean('batch', { default: config.validateBatch });
        parser.json('params');
        parser.json('headers');
        parser.include(watchOptions, { defaults: { event: 'change' } });
        return options;
    }
    function disableContainerTemp(container) {
        let controls = findFieldsAndButtons(container);
        return u.sequence(u.map(controls, disableControlTemp));
    }
    function disableControlTemp(control) {
        if (control.disabled)
            return;
        let focusFallback;
        if (document.activeElement === control) {
            focusFallback = findGroup(control);
            control.disabled = true;
            up.focus(focusFallback, { force: true, preventScroll: true });
        }
        else {
            control.disabled = true;
        }
        return () => { control.disabled = false; };
    }
    function getDisableContainers(disable, origin) {
        let originScope = () => getRegion(origin);
        if (disable === true) {
            return [originScope()];
        }
        else if (u.isElement(disable)) {
            return [disable];
        }
        else if (u.isString(disable)) {
            return up.fragment.subtree(originScope(), disable, { origin });
        }
        else if (u.isArray(disable)) {
            return u.flatMap(disable, (d) => getDisableContainers(d, origin));
        }
        else {
            return [];
        }
    }
    function getDisablePreviewFn(disable, origin) {
        return function (preview) {
            let containers = getDisableContainers(disable, origin);
            for (let container of containers) {
                preview.disable(container);
            }
        };
    }
    function setContainerDisabled(container, disabled) {
        for (let control of findFieldsAndButtons(container)) {
            control.disabled = disabled;
        }
    }
    function destinationOptions(form, options, parserOptions) {
        var _a;
        options = u.options(options);
        form = getForm(form);
        const parser = new up.OptionsParser(form, options, parserOptions);
        parser.string('contentType', { attr: 'enctype' });
        parser.json('headers');
        const paramParts = [
            up.Params.fromForm(form),
            e.jsonAttr(form, 'up-params'),
        ];
        const headerParts = [
            e.jsonAttr(form, 'up-headers'),
        ];
        const submitButton = ((_a = options.submitButton) !== null && _a !== void 0 ? _a : (options.submitButton = findSubmitButtons(form)[0]));
        if (submitButton) {
            paramParts.push(up.Params.fromFields(submitButton), e.jsonAttr(submitButton, 'up-params'));
            headerParts.push(e.jsonAttr(submitButton, 'up-headers'));
            options.method || (options.method = submitButton.getAttribute('formmethod'));
            options.url || (options.url = submitButton.getAttribute('formaction'));
        }
        options.params = up.Params.merge(...paramParts, options.params);
        options.headers = u.merge(...headerParts, options.headers);
        parser.string('url', { attr: 'action', default: up.fragment.source(form) });
        parser.string('method', {
            attr: ['up-method', 'data-method', 'method'],
            default: 'GET',
            normalize: u.normalizeMethod
        });
        if (options.method === 'GET') {
            options.url = up.Params.stripURL(options.url);
        }
        return options;
    }
    function submitButtonOverrides(submitButton) {
        if (!submitButton)
            return {};
        let followOptions = up.link.followOptions(submitButton, {}, { defaults: false });
        return u.omit(followOptions, ['method', 'url', 'guardEvent', 'origin', 'params', 'headers']);
    }
    function watch(...args) {
        let [root, options, callback] = u.args(args, 'val', 'options', 'callback');
        root = up.element.get(root);
        callback || (callback = watchCallbackFromElement(root) || up.fail('No callback given for up.watch()'));
        const watcher = new up.FieldWatcher(root, options, callback);
        return watcher.start();
    }
    function watchCallbackFromElement(element) {
        return up.script.callbackAttr(element, 'up-watch', { argNames: ['value', 'name', 'options'] });
    }
    function autosubmit(target, options = {}) {
        const onChange = (_diff, renderOptions) => submit(target, renderOptions);
        return watch(target, Object.assign(Object.assign({ logPrefix: 'up.autosubmit()' }, options), { batch: true }), onChange);
    }
    function getGroupSelectors() {
        var _a, _b;
        return ((_b = (_a = up.migrate).migratedFormGroupSelectors) === null || _b === void 0 ? void 0 : _b.call(_a)) || config.groupSelectors;
    }
    function findGroup(field) {
        return findGroupSolution(field).element;
    }
    function findGroupSolution(field) {
        return u.findResult(getGroupSelectors(), function (groupSelector) {
            let group = field.closest(groupSelector);
            if (group) {
                let strongDerivedGroupTarget = up.fragment.tryToTarget(group, { strong: true });
                let goodDerivedFieldTarget = up.fragment.tryToTarget(field);
                let groupHasFieldTarget = goodDerivedFieldTarget && (group !== field) && `${groupSelector}:has(${goodDerivedFieldTarget})`;
                let target = strongDerivedGroupTarget || groupHasFieldTarget;
                if (target) {
                    return {
                        target,
                        element: group,
                        origin: field
                    };
                }
            }
        });
    }
    function validate(...args) {
        let options = parseValidateArgs(...args);
        let form = getForm(options.origin);
        let validator = getFormValidator(form);
        return validator.validate(options);
    }
    function parseValidateArgs(originOrTarget, ...args) {
        const options = u.extractOptions(args);
        if (options.origin) {
            options.target || (options.target = up.fragment.toTarget(originOrTarget));
        }
        else {
            options.origin || (options.origin = up.fragment.get(originOrTarget));
        }
        return options;
    }
    function getForm(element) {
        return getLiteralForm(element) || getClosestForm(element) || getAssociatedForm(element);
    }
    function getLiteralForm(element) {
        if (element instanceof HTMLFormElement)
            return element;
    }
    function getClosestForm(element) {
        return element.closest('form');
    }
    function getAssociatedForm(element) {
        let formID = element.getAttribute('form');
        if (formID) {
            let selector = 'form' + e.idSelector(formID);
            return up.fragment.get(selector, { layer: element });
        }
    }
    function getFormValidator(form) {
        return form.upFormValidator || (form.upFormValidator = setupFormValidator(form));
    }
    function setupFormValidator(form) {
        const validator = new up.FormValidator(form);
        const stop = validator.start();
        up.destructor(form, stop);
        return validator;
    }
    function getRegion(origin) {
        return getOriginRegion(origin) || up.layer.current.element;
    }
    function getOriginRegion(origin) {
        var _a;
        if (origin) {
            return getForm(origin) || ((_a = up.layer.get(origin)) === null || _a === void 0 ? void 0 : _a.element);
        }
    }
    const trackFields = up.mockable(function (...args) {
        let [root, { guard }, callback] = u.args(args, 'val', 'options', 'callback');
        let filter = function (fields) {
            let region = getRegion(root);
            return u.filter(fields, function (field) {
                return (root === region || root.contains(field))
                    && (getRegion(field) === region)
                    && (!guard || guard(field));
            });
        };
        return up.fragment.trackSelector(fieldSelector(), { filter }, callback);
    });
    function focusedField() {
        return u.presence(document.activeElement, isField);
    }
    function isSubmittable(form) {
        form = up.fragment.get(form);
        return config.matches(form, 'submitSelectors');
    }
    up.on('submit', config.selectorFn('submitSelectors'), function (event, form) {
        const submitButton = u.presence(event.submitter, isSubmitButton);
        if ((submitButton === null || submitButton === void 0 ? void 0 : submitButton.getAttribute('up-submit')) === 'false')
            return;
        if (event.defaultPrevented)
            return;
        up.event.halt(event, { log: true });
        up.error.muteUncriticalRejection(submit(form, { submitButton }));
    });
    up.compiler('[up-switch]', (switcher) => {
        return new up.Switcher(switcher).start();
    });
    up.attribute('up-watch', (formOrField) => watch(formOrField));
    up.compiler('[up-validate]', { rerun: true }, function (element) {
        let form = getForm(element);
        if (form)
            getFormValidator(form);
    });
    up.attribute('up-autosubmit', (formOrField) => autosubmit(formOrField, { logPrefix: '[up-autosubmit]' }));
    return {
        config,
        submit,
        submitOptions,
        destinationOptions,
        submitButtonOverrides,
        watchOptions,
        validateOptions,
        isSubmittable,
        watch,
        validate,
        autosubmit,
        fieldSelector,
        fields: findFields,
        trackFields,
        isField,
        submitButtons: findSubmitButtons,
        focusedField,
        disableTemp: disableContainerTemp,
        setDisabled: setContainerDisabled,
        getDisablePreviewFn,
        group: findGroup,
        groupSolution: findGroupSolution,
        groupSelectors: getGroupSelectors,
        get: getForm,
        getRegion,
    };
})();
up.submit = up.form.submit;
up.watch = up.form.watch;
up.autosubmit = up.form.autosubmit;
up.validate = up.form.validate;
